import React, { useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import { Alert, Badge, Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import {
  fetchAllOrganizations,
  type OrganizationRecord,
} from "../../../api/organizations";
import {
  fetchIntelligenceShipGate,
  updateIntelligenceShipGate,
} from "../../../api/intelligenceShipGate";
import "./IntelligenceGlass.css";

interface Props {
  show: boolean;
  user: User;
  onHide: () => void;
}

export const IntelligenceShipGateModal: React.FC<Props> = ({
  show,
  user,
  onHide,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [productEnabled, setProductEnabled] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [source, setSource] = useState<string | undefined>();
  const [organizations, setOrganizations] = useState<OrganizationRecord[]>([]);
  const [filter, setFilter] = useState("");
  const [manualOrgId, setManualOrgId] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const [gate, orgs] = await Promise.all([
        fetchIntelligenceShipGate(user),
        fetchAllOrganizations(100),
      ]);
      setProductEnabled(gate.productEnabled);
      setSelectedIds(gate.previewOrganizationIds);
      setSource(gate.source);
      setOrganizations(orgs);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to load Intelligence availability"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when modal opens
  }, [show, user]);

  const orgById = useMemo(() => {
    const map = new Map<string, OrganizationRecord>();
    for (const org of organizations) map.set(org.id, org);
    return map;
  }, [organizations]);

  const filteredOrgs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return organizations.slice(0, 100);
    return organizations
      .filter((org) => {
        const hay = `${org.id} ${org.name || ""} ${org.email || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 100);
  }, [organizations, filter]);

  const toggleOrg = (orgId: string) => {
    setSelectedIds((prev) =>
      prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]
    );
  };

  const addManualOrgId = () => {
    const oid = manualOrgId.trim();
    if (!oid) return;
    setSelectedIds((prev) => (prev.includes(oid) ? prev : [...prev, oid]));
    setManualOrgId("");
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const gate = await updateIntelligenceShipGate(user, {
        productEnabled,
        previewOrganizationIds: selectedIds,
      });
      setProductEnabled(gate.productEnabled);
      setSelectedIds(gate.previewOrganizationIds);
      setSource(gate.source);
      setSuccess("Intelligence availability saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="intel-glass-modal">
      <Modal.Header closeButton>
        <Modal.Title>Intelligence availability</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Control who can see Intelligence in admin. Keep it off globally and
          allowlist pilot organizations, or enable for everyone.
        </p>

        {error && (
          <Alert variant="danger" className="py-2">
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" className="py-2">
            {success}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            <Form.Check
              type="switch"
              id="intel-product-enabled"
              className="mb-3"
              label={
                <span>
                  <strong>Enable for all organizations</strong>
                  <span className="d-block text-muted small">
                    When off, only the allowlisted orgs below see Intelligence.
                  </span>
                </span>
              }
              checked={productEnabled}
              onChange={(e) => setProductEnabled(e.target.checked)}
            />

            {source && (
              <p className="small text-muted mb-3">
                Current source: <Badge bg="secondary">{source}</Badge>
                {source === "env" && (
                  <span className="ms-1">
                    (env fallback — save here to manage from the panel)
                  </span>
                )}
              </p>
            )}

            <fieldset disabled={productEnabled} className="mb-2">
              <Form.Label className="fw-semibold">
                Allowlisted organizations ({selectedIds.length})
              </Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  placeholder="Add org id manually…"
                  value={manualOrgId}
                  onChange={(e) => setManualOrgId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addManualOrgId();
                    }
                  }}
                  disabled={productEnabled}
                />
                <Button
                  variant="outline-secondary"
                  onClick={addManualOrgId}
                  disabled={productEnabled || !manualOrgId.trim()}
                >
                  Add
                </Button>
              </InputGroup>
              <Form.Control
                className="mb-2"
                placeholder="Search orgs by name, email, or id…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                disabled={productEnabled}
              />
              <div
                className="border rounded p-2"
                style={{ maxHeight: 260, overflowY: "auto" }}
              >
                {filteredOrgs.length === 0 ? (
                  <p className="text-muted small mb-0">No organizations match.</p>
                ) : (
                  filteredOrgs.map((org) => (
                    <Form.Check
                      key={org.id}
                      type="checkbox"
                      id={`intel-org-${org.id}`}
                      className="mb-1"
                      checked={selectedIds.includes(org.id)}
                      onChange={() => toggleOrg(org.id)}
                      label={
                        <span>
                          <strong>{org.name || org.id}</strong>
                          <span className="text-muted small ms-1">{org.id}</span>
                        </span>
                      }
                    />
                  ))
                )}
              </div>
              {selectedIds.length > 0 && (
                <div className="mt-2 d-flex flex-wrap gap-1">
                  {selectedIds.map((id) => (
                    <Badge
                      key={id}
                      bg="primary"
                      style={{ cursor: productEnabled ? "default" : "pointer" }}
                      title={productEnabled ? undefined : "Click to remove"}
                      onClick={() => !productEnabled && toggleOrg(id)}
                    >
                      {orgById.get(id)?.name || id} ×
                    </Badge>
                  ))}
                </div>
              )}
            </fieldset>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Close
        </Button>
        <Button variant="primary" onClick={() => void handleSave()} disabled={loading || saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
